import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, X, Edit, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { referralContractService, serviceCatalogService } from '../../lib/services';
import { useStore } from '../../lib/store';
import { can, getUserRoleFromDB, type UserRole } from '../../lib/rbac';

interface ReferralContract {
  id: string;
  referrer_name: string;
  referrer_type: 'external_doctor' | 'partner_clinic' | 'agent';
  service_id: string;
  fee_type: 'percent' | 'fixed';
  fee_value: number;
  active: boolean;
  created_at: string;
  updated_at: string;
  service?: {
    code: string;
    name: string;
    type: string;
  };
}

interface ServiceCatalogItem {
  id: string;
  code: string;
  name: string;
  type: string;
}

interface ContractFormData {
  referrer_name: string;
  referrer_type: 'external_doctor' | 'partner_clinic' | 'agent';
  service_id: string;
  fee_type: 'percent' | 'fixed';
  fee_value: number;
  active: boolean;
}

const ReferralContracts: React.FC = () => {
  const [contracts, setContracts] = useState<ReferralContract[]>([]);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContract, setEditingContract] = useState<ReferralContract | null>(null);
  const [filterReferrerType, setFilterReferrerType] = useState<string>('all');
  const [userRole, setUserRole] = useState<UserRole>('receptionist');
  const { user } = useStore();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContractFormData>();

  useEffect(() => {
    loadUserRole();
    loadData();
  }, []);

  const loadUserRole = async () => {
    if (!user?.id) return;
    try {
      const role = await getUserRoleFromDB(user.id);
      setUserRole(role || 'receptionist');
    } catch (error) {
      console.error('Error loading user role:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [contractsData, servicesData] = await Promise.all([
        referralContractService.getAll(),
        serviceCatalogService.getAll(),
      ]);
      setContracts(contractsData);
      setServices(servicesData.filter((s: any) => s.active));
    } catch (error: any) {
      toast.error(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const canManage = can(userRole, 'manage_billing');

  const onAddSubmit = async (data: ContractFormData) => {
    try {
      await referralContractService.create({
        referrer_name: data.referrer_name,
        referrer_type: data.referrer_type,
        service_id: data.service_id,
        fee_type: data.fee_type,
        fee_value: Number(data.fee_value),
        active: data.active ?? true,
      });
      toast.success('Referral contract created successfully');
      setShowAddModal(false);
      reset();
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create contract');
    }
  };

  const onEditSubmit = async (data: ContractFormData) => {
    if (!editingContract) return;
    try {
      await referralContractService.update(editingContract.id, {
        referrer_name: data.referrer_name,
        referrer_type: data.referrer_type,
        fee_type: data.fee_type,
        fee_value: Number(data.fee_value),
        active: data.active,
      });
      toast.success('Contract updated successfully');
      setShowEditModal(false);
      setEditingContract(null);
      reset();
      loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update contract');
    }
  };

  const handleEdit = (contract: ReferralContract) => {
    setEditingContract(contract);
    reset({
      referrer_name: contract.referrer_name,
      referrer_type: contract.referrer_type,
      service_id: contract.service_id,
      fee_type: contract.fee_type,
      fee_value: contract.fee_value,
      active: contract.active,
    });
    setShowEditModal(true);
  };

  const filteredContracts = contracts.filter(contract => {
    if (filterReferrerType === 'all') return true;
    return contract.referrer_type === filterReferrerType;
  });

  const getReferrerTypeBadge = (type: string) => {
    const badges: Record<string, string> = {
      external_doctor: 'bg-blue-100 text-blue-800',
      partner_clinic: 'bg-green-100 text-green-800',
      agent: 'bg-purple-100 text-purple-800',
    };
    return badges[type] || 'bg-gray-100 text-gray-800';
  };

  const formatReferrerType = (type: string) => {
    const labels: Record<string, string> = {
      external_doctor: 'External Doctor',
      partner_clinic: 'Partner Clinic',
      agent: 'Agent',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading contracts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Referral Contracts</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage referral fee agreements with external partners
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => {
              reset({
                referrer_name: '',
                referrer_type: 'external_doctor',
                service_id: '',
                fee_type: 'percent',
                fee_value: 0,
                active: true,
              });
              setShowAddModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Contract
          </button>
        )}
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <label className="text-sm font-medium text-gray-700">Filter by Referrer Type:</label>
            <select
              value={filterReferrerType}
              onChange={(e) => setFilterReferrerType(e.target.value)}
              className="ml-2 block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="all">All Types</option>
              <option value="external_doctor">External Doctor</option>
              <option value="partner_clinic">Partner Clinic</option>
              <option value="agent">Agent</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Referrer Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Referrer Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fee Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fee Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                {canManage && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContracts.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 7 : 6} className="px-6 py-4 text-center text-sm text-gray-500">
                    No referral contracts found
                  </td>
                </tr>
              ) : (
                filteredContracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{contract.referrer_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getReferrerTypeBadge(contract.referrer_type)}`}>
                        {formatReferrerType(contract.referrer_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contract.service ? `${contract.service.code} - ${contract.service.name}` : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">{contract.fee_type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contract.fee_type === 'percent' ? `${contract.fee_value}%` : `₹${contract.fee_value.toLocaleString()}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        contract.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {contract.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleEdit(contract)}
                          className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add New Referral Contract</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  reset();
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onAddSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Referrer Name *
                  </label>
                  <input
                    type="text"
                    {...register('referrer_name', { required: 'Referrer name is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Dr. Smith"
                  />
                  {errors.referrer_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.referrer_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Referrer Type *
                  </label>
                  <select
                    {...register('referrer_type', { required: 'Type is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="external_doctor">External Doctor</option>
                    <option value="partner_clinic">Partner Clinic</option>
                    <option value="agent">Agent</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service *
                  </label>
                  <select
                    {...register('service_id', { required: 'Service is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.code} - {service.name}
                      </option>
                    ))}
                  </select>
                  {errors.service_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.service_id.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fee Type *
                  </label>
                  <select
                    {...register('fee_type', { required: 'Fee type is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="percent">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fee Value *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('fee_value', { 
                      required: 'Fee value is required',
                      min: { value: 0, message: 'Fee must be non-negative' }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                  {errors.fee_value && (
                    <p className="mt-1 text-sm text-red-600">{errors.fee_value.message}</p>
                  )}
                </div>

                <div className="col-span-2 flex items-center">
                  <input
                    type="checkbox"
                    {...register('active')}
                    defaultChecked={true}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Active
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    reset();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editingContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Referral Contract</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingContract(null);
                  reset();
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Referrer Name *
                  </label>
                  <input
                    type="text"
                    {...register('referrer_name', { required: 'Referrer name is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.referrer_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.referrer_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Referrer Type *
                  </label>
                  <select
                    {...register('referrer_type', { required: 'Type is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="external_doctor">External Doctor</option>
                    <option value="partner_clinic">Partner Clinic</option>
                    <option value="agent">Agent</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service
                  </label>
                  <select
                    {...register('service_id')}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                  >
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.code} - {service.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Service cannot be changed after creation</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fee Type *
                  </label>
                  <select
                    {...register('fee_type', { required: 'Fee type is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="percent">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fee Value *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('fee_value', { 
                      required: 'Fee value is required',
                      min: { value: 0, message: 'Fee must be non-negative' }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.fee_value && (
                    <p className="mt-1 text-sm text-red-600">{errors.fee_value.message}</p>
                  )}
                </div>

                <div className="col-span-2 flex items-center">
                  <input
                    type="checkbox"
                    {...register('active')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Active
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingContract(null);
                    reset();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralContracts;
